import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';
import { ExecuteFlowletTransformation } from 'app/model/dataflow/executeflowlettransformation';
import { TransformationAdapter } from 'app/model/adapters/dataflow/transformations/base-transformation.adapter';
import { FLOW, IFlowletTransformation } from 'app/model/adapters/dataflow/IDataflow';
import { DataFlowSchemaAdapterUtils } from '../dataflow-schema-adapter-utils';
import { GlobalVariables } from 'app/services/globals';
import { ReferenceAdapter } from '../../reference.adapter';
import { convertParametersToModel, fillParameterValuesForResource } from 'app/shared/utils/parameter-converter.util';
import { Flowlet } from 'app/model/dataflow/flowlet';
import { getCachedServiceInstanceSync } from 'app/multi-contexts/modules/root/root.application';
import { LoggerService } from 'app/logger/logger.service';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(ExecuteFlowletTransformation),
    asynchronous: true
})
export class ExecuteFlowletTransformationAdapter extends TransformationAdapter<ExecuteFlowletTransformation> {
    public async getDslScript(executeFlowletTransformation: ExecuteFlowletTransformation): Promise<string> {
        const props: string[] = [
            this.getParametersDSL(executeFlowletTransformation),
            this.getColumnsFromSchemaMap(executeFlowletTransformation),
            `composition: '${executeFlowletTransformation.flowlet && executeFlowletTransformation.flowlet.name}'`
        ];

        const partition = TransformationAdapter.getPartitionDslScript(executeFlowletTransformation);
        if (partition) {
            props.push(partition);
        }

        const outputStreams: string[] = [];
        executeFlowletTransformation.outputStreams.forEach((outputStream) => outputStreams.push(outputStream.name));

        const knownProps = props.filter((prop) => !!prop).join(',\n\t');
        const unknownProps = this._getUnknownProps(executeFlowletTransformation, knownProps);
        const transformationData = [knownProps, unknownProps].filter((c) => !!c).join(',\n\t');

        return (
            `${this.getInputStreamsWithDuplicates(executeFlowletTransformation)}compose(${transformationData}) ` +
            `${FLOW} ${executeFlowletTransformation.name}@(${outputStreams.join(', ')})`.trimLeft()
        );
    }

    protected async _toResource(transformation: ExecuteFlowletTransformation): Promise<IFlowletTransformation> {
        const resource: IFlowletTransformation = {
            name: transformation.name,
            description: transformation.description
        };

        if (transformation.flowlet) {
            resource.flowlet = this._toDataFlowReferenceModel(transformation);
        }

        if (GlobalVariables.splitScriptEnabled) {
            resource.script = await this.getDslScript(transformation);
        }

        return resource;
    }

    protected async _toModel(
        resource: IFlowletTransformation,
        model?: ExecuteFlowletTransformation
    ): Promise<ExecuteFlowletTransformation> {
        model.description = resource.description;

        if (resource.flowlet) {
            model.flowlet = ReferenceAdapter.toProxyModel<Flowlet>(Metamodel.modelFor(Flowlet), resource.flowlet, this.context);
            if (resource.flowlet.parameters) {
                convertParametersToModel(
                    model,
                    !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                    model.flowlet,
                    model.parameters,
                    resource.flowlet.parameters
                );
            }
        }

        return model;
    }

    private _toDataFlowReferenceModel(transformation: ExecuteFlowletTransformation): Model.DataFlowReference {
        const dataflowReference = ReferenceAdapter.toResource(
            transformation.flowlet,
            this.context
        ) as Model.DataFlowReference;
        dataflowReference.type = 'DataFlowReference';
        dataflowReference.parameters = fillParameterValuesForResource(transformation.parameters, this.context,
            getCachedServiceInstanceSync<LoggerService>(this.context.contextData, 'LoggerService'));
        return dataflowReference;
    }

    private getParametersDSL(model: ExecuteFlowletTransformation): string {
        if (model.parameters && model.parameters.length > 0) {
            const parameterStrings = model.parameters.map(
                (parameterValue) => `${parameterValue.parameter.name} = ${parameterValue.value}`
            );
            return `mapParameter(\n\t\t${parameterStrings.join(',\n\t\t')}\n\t)`;
        }
        return '';
    }

    private getColumnsFromSchemaMap(model: ExecuteFlowletTransformation): string {
        const columns: string[] = [];
        model.schemaMap.forEach((value, key) => {
            columns[key] = DataFlowSchemaAdapterUtils.convertSchemaToColumns(value);
        });
        return columns.join();
    }

    private getInputStreamsWithDuplicates(model: ExecuteFlowletTransformation): string {
        const inputStreams: string[] = [];
        model.inputStreamsWithDuplicates.forEach((inputStream) => inputStreams.push(inputStream.qualifiedName));
        if (inputStreams.length) {
            return inputStreams.join(', ').concat(' ');
        }
        return '';
    }
}
